import type { DeferredCredit, Ticket } from "./types";
import type { FieldMutation } from "./db";
import { onsiteTicketPlan } from "./settle";

/**
 * Compile-time checklist: every FieldMutation kind is durable and must hit the
 * outbox on the same user gesture in field mode (never Dexie/`state` alone).
 * Adding a kind to FieldMutation without updating this Record fails `tsc`.
 */
export const FIELD_MUTATION_REQUIRES_OUTBOX: Record<FieldMutation["kind"], true> = {
  checkin: true,
  assignment: true,
  field_ticket_create: true,
  field_ticket_add_seat: true,
  ticket_ops: true,
  fee_payout: true,
  cash_count: true,
  settle_topup: true,
  settle_refund: true,
  coupon_issue: true,
  redeem_deferred: true,
};

export function fieldMutationRequiresOutbox(kind: FieldMutation["kind"]): true {
  return FIELD_MUTATION_REQUIRES_OUTBOX[kind];
}

/** Pre-stable draft id used only before field create is enqueued. */
export function isLocalDraftTicketId(id: string): boolean {
  return id.startsWith("local-");
}

/** Apply a field mutation onto an in-memory ticket list (optimistic). */
export function applyFieldMutationLocally(tickets: Ticket[], mutation: FieldMutation): Ticket[] {
  if (mutation.kind === "checkin" || mutation.kind === "assignment") {
    return tickets.map((ticket) => {
      if (ticket.id !== mutation.ticketId) return ticket;
      const units = (ticket.units || []).map((unit) => {
        if (unit.id !== mutation.unitId) return unit;
        if (mutation.kind === "assignment") {
          return {
            ...unit,
            performer: mutation.performer,
            updatedAt: mutation.createdAt,
            device: mutation.deviceId,
          };
        }
        return {
          ...unit,
          checkedIn: mutation.delta === 1,
          updatedAt: mutation.createdAt,
          device: mutation.deviceId,
        };
      });
      return {
        ...ticket,
        units,
        checkIns: {
          afternoon: {
            count: units.filter((unit) => unit.session === "afternoon" && unit.checkedIn).length,
          },
          evening: {
            count: units.filter((unit) => unit.session === "evening" && unit.checkedIn).length,
          },
        },
      };
    });
  }

  if (mutation.kind === "field_ticket_create") {
    if (tickets.some((ticket) => ticket.id === mutation.ticketId)) return tickets;
    return [...tickets, mutation.ticket];
  }

  if (mutation.kind === "field_ticket_add_seat") {
    return tickets.map((ticket) => {
      if (ticket.id !== mutation.ticketId) return ticket;
      const units = [...(ticket.units || [])];
      const bySession = new Map<string, number>();
      for (const unit of units) {
        bySession.set(unit.session, Math.max(bySession.get(unit.session) || 0, unit.seat));
      }
      const addCount = Math.max(1, mutation.seats);
      for (const session of ticket.sessions) {
        let seat = bySession.get(session) || 0;
        for (let i = 0; i < addCount; i += 1) {
          seat += 1;
          units.push({
            id: `${ticket.id}:${seat}:${session}`,
            seat,
            session,
            performer: "尚未指名",
            checkedIn: false,
            updatedAt: mutation.createdAt,
            device: mutation.deviceId,
          });
        }
        bySession.set(session, seat);
      }
      const quantity = Math.max(...[...bySession.values()], ticket.quantity);
      return { ...ticket, units, quantity };
    });
  }

  if (mutation.kind === "ticket_ops") {
    return tickets.map((ticket) => {
      if (ticket.id !== mutation.ticketId) return ticket;
      const plan = onsiteTicketPlan(ticket, mutation.opsStatus);
      if (plan) {
        const units = (ticket.units || []).filter((unit) => plan.sessions.includes(unit.session));
        for (const session of plan.sessions) {
          for (let seat = 1; seat <= Math.max(1, ticket.quantity); seat += 1) {
            if (units.some((unit) => unit.session === session && unit.seat === seat)) continue;
            units.push({
              id: `${ticket.id}:${seat}:${session}`,
              seat,
              session,
              performer: "尚未指名",
              checkedIn: false,
              updatedAt: mutation.createdAt,
              device: mutation.deviceId,
            });
          }
        }
        return {
          ...ticket,
          ...plan,
          originalTicketType: ticket.originalTicketType || ticket.ticketType,
          kind: "onsite" as const,
          settlePaidAtDoor: false,
          units,
          checkIns: {
            afternoon: {
              count: units.filter((unit) => unit.session === "afternoon" && unit.checkedIn).length,
            },
            evening: {
              count: units.filter((unit) => unit.session === "evening" && unit.checkedIn).length,
            },
          },
        };
      }
      return {
        ...ticket,
        opsStatus: mutation.opsStatus as Ticket["opsStatus"],
        kind: mutation.opsStatus === "refund" ? "refund" : ticket.kind,
      };
    });
  }

  if (mutation.kind === "settle_topup") {
    return tickets.map((ticket) => {
      if (ticket.id !== mutation.ticketId) return ticket;
      return {
        ...ticket,
        settleStatus: "topup_paid",
        paymentRecorded: true,
        settlePaidAtDoor: true,
      };
    });
  }

  if (mutation.kind === "settle_refund") {
    return tickets.map((ticket) => {
      if (ticket.id !== mutation.ticketId) return ticket;
      return { ...ticket, settleStatus: "refunded", settlePaidAtDoor: true };
    });
  }

  if (mutation.kind === "coupon_issue") {
    return tickets.map((ticket) => {
      if (ticket.id !== mutation.ticketId) return ticket;
      return { ...ticket, couponIssued: true };
    });
  }

  return tickets;
}

export function applyDeferredLocally(
  credits: DeferredCredit[],
  mutation: FieldMutation,
): DeferredCredit[] {
  if (mutation.kind === "redeem_deferred") {
    return credits.map((credit) => {
      if (credit.id !== mutation.creditSourceId) return credit;
      return {
        ...credit,
        redeemed: true,
        redeemedTicketId: mutation.ticketId,
        couponIssued: mutation.couponIssued ? true : credit.couponIssued,
      };
    });
  }
  if (mutation.kind === "coupon_issue" && mutation.creditSourceId) {
    return credits.map((credit) => {
      if (credit.id !== mutation.creditSourceId) return credit;
      return { ...credit, couponIssued: true };
    });
  }
  return credits;
}

/**
 * After checkpoint pull replaces Dexie with the server snapshot, keep local-only
 * rows that the pull does not yet contain:
 * - `local-*` orphan drafts (legacy / crash mid-create; primary paths must not create these)
 * - tickets still referenced by pending outbox
 * - tickets we just tried to create but the snapshot omitted (retainTicketIds)
 */
export function mergeTicketsAfterCheckpointPull(input: {
  beforeTickets: Ticket[];
  serverTickets: Ticket[];
  pendingTicketIds: Set<string>;
  retainTicketIds?: Set<string>;
}): Ticket[] {
  const serverIds = new Set(input.serverTickets.map((ticket) => ticket.id));
  const retain = input.retainTicketIds || new Set<string>();
  const preserved = input.beforeTickets.filter(
    (ticket) =>
      !serverIds.has(ticket.id) &&
      (isLocalDraftTicketId(ticket.id) ||
        input.pendingTicketIds.has(ticket.id) ||
        retain.has(ticket.id)),
  );
  return [...preserved, ...input.serverTickets];
}

export function canWriteCash(role: string): boolean {
  return role === "cash" || role === "general";
}

export function canWriteFees(role: string): boolean {
  return role === "fees" || role === "general";
}

export function canSellField(role: string): boolean {
  return role === "field_sales" || role === "general";
}
